import React, { useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import type { EconomicDashboard, ProfessionalSettlement, Quote, SellerSettlement, StaffUser, CommercialProduct, Professional } from "../types";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { formatCurrency } from "../lib/format";

function formatPct(n: number | undefined | null) {
  if (n === undefined || n === null) return "-";
  return `${(n * 100).toFixed(1)}%`;
}

export default function Economia() {
  const { user, isAdminLike } = useAuth();
  const { show } = useToast();

  const allowed = !!user && ["admin", "director", "sales_manager", "marketing"].includes(user.role_code);

  const [dashboard, setDashboard] = useState<EconomicDashboard | null>(null);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [products, setProducts] = useState<CommercialProduct[]>([]);
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [sellerSettlements, setSellerSettlements] = useState<SellerSettlement[]>([]);
  const [professionalSettlements, setProfessionalSettlements] = useState<ProfessionalSettlement[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  function reload() {
    if (!allowed) return;
    setLoading(true);
    Promise.all([
      api.getDashboardEconomic(),
      api.getQuotes().catch(() => []),
      api.getCommercialProducts().catch(() => []),
      api.users().catch(() => []),
      api.getProfessionals().catch(() => []),
      api.getSellerSettlements().catch(() => []),
      api.getProfessionalSettlements().catch(() => []),
    ])
      .then(([d, q, p, u, pr, ss, ps]) => {
        setDashboard(d);
        setQuotes(q);
        setProducts(p);
        setUsers(u);
        setProfessionals(pr);
        setSellerSettlements(ss);
        setProfessionalSettlements(ps);
      })
      .catch((e) => {
        if (e instanceof ApiClientError) show(e.message, "error");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  if (!user) return null;
  if (!allowed) {
    return (
      <div className="dashboard-page">
        <h1>Economía</h1>
        <p className="meta">No tenés permisos para ver el dashboard económico de Dirección.</p>
      </div>
    );
  }

  function userName(id: any) {
    return users.find((u) => String(u.id) === String(id))?.full_name || `#${id}`;
  }
  function professionalName(id: any) {
    return professionals.find((p) => String(p.id) === String(id))?.full_name || `#${id}`;
  }

  async function generateSellerSettlements() {
    setBusyId("gen-sellers");
    try {
      await api.generateSellerSettlements();
      show("Liquidaciones de vendedoras generadas.", "success");
      reload();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
    } finally {
      setBusyId(null);
    }
  }

  async function approveSellerSettlement(id: any) {
    setBusyId(`approve-${id}`);
    try {
      await api.approveSellerSettlement(id);
      show("Liquidación aprobada.", "success");
      reload();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
    } finally {
      setBusyId(null);
    }
  }

  async function lockSellerSettlement(id: any) {
    setBusyId(`lock-${id}`);
    try {
      await api.lockSellerSettlement(id);
      show("Liquidación bloqueada.", "success");
      reload();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
    } finally {
      setBusyId(null);
    }
  }

  async function generateProfessionalSettlements() {
    setBusyId("gen-professionals");
    try {
      await api.generateProfessionalSettlements();
      show("Fondo de integración generado.", "success");
      reload();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
    } finally {
      setBusyId(null);
    }
  }

  async function approveProfessionalSettlement(id: any) {
    setBusyId(`papprove-${id}`);
    try {
      await api.approveProfessionalSettlement(id);
      show("Liquidación de profesional aprobada.", "success");
      reload();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
    } finally {
      setBusyId(null);
    }
  }

  async function lockProfessionalSettlement(id: any) {
    setBusyId(`plock-${id}`);
    try {
      await api.lockProfessionalSettlement(id);
      show("Liquidación de profesional bloqueada.", "success");
      reload();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
    } finally {
      setBusyId(null);
    }
  }

  // Quotes come back id DESC (most recent first) from GET /quotes; cap the
  // table to the most recent 20 so a long-lived account doesn't render an
  // unbounded list here.
  const protectedQuotes = quotes.filter((q) => q.protected_floor !== null && q.protected_floor !== undefined).slice(0, 20);

  return (
    <div className="dashboard-page economia-page">
      <h1>Economía</h1>
      <p className="meta">Facturación, BCM, comisiones y contribución de Mauvelore, reconciliados directamente de los cobros.</p>

      {loading && <div className="loading-strip">Cargando métricas...</div>}

      {dashboard && (
        <div className="kpi-cards">
          <div className="kpi-card">
            <div className="muted">Facturación cobrada</div>
            <div className="big">{formatCurrency(dashboard.facturacion_cobrada)}</div>
          </div>
          <div className="kpi-card">
            <div className="muted">BCM</div>
            <div className="big">{formatCurrency(dashboard.bcm)}</div>
            <div className="muted">Margen {formatPct(dashboard.margen_bcm_pct)}</div>
          </div>
          <div className="kpi-card">
            <div className="muted">Comisiones comerciales</div>
            <div className="big">{formatCurrency(dashboard.comisiones)}</div>
          </div>
          <div className="kpi-card">
            <div className="muted">Contribución Mauvelore</div>
            <div className="big">{formatCurrency(dashboard.contribucion_mauvelore)}</div>
          </div>
        </div>
      )}

      {dashboard && (
        <div className="dashboard-metrics" style={{ marginTop: 16 }}>
          <div className="metric-tile" style={{ cursor: "default" }}>
            <div className="label">Facturación emitida</div>
            <div className="value">{formatCurrency(dashboard.facturacion_emitida)}</div>
          </div>
          <div className="metric-tile" style={{ cursor: "default" }}>
            <div className="label">Ticket promedio</div>
            <div className="value">{formatCurrency(dashboard.ticket_promedio)}</div>
          </div>
          <div className="metric-tile" style={{ cursor: "default" }}>
            <div className="label">Costo financiero</div>
            <div className="value">{formatCurrency(dashboard.costo_financiero)}</div>
          </div>
          <div className="metric-tile" style={{ cursor: "default" }}>
            <div className="label">Descuentos otorgados</div>
            <div className="value">{formatCurrency(dashboard.descuentos)}</div>
          </div>
          <div className="metric-tile" style={{ cursor: "default" }}>
            <div className="label">Excepciones de piso</div>
            <div className="value">{dashboard.excepciones_piso}</div>
          </div>
          <div className="metric-tile" style={{ cursor: "default" }}>
            <div className="label">Fondo de integración</div>
            <div className="value">{formatCurrency(dashboard.fondo_integracion)}</div>
          </div>
          <div className="metric-tile" style={{ cursor: "default" }}>
            <div className="label">Conversión</div>
            <div className="value">{formatPct(dashboard.conversion_pct)}</div>
          </div>
          <div className="metric-tile" style={{ cursor: "default" }}>
            <div className="label">Pipeline abierto</div>
            <div className="value">{dashboard.pipeline_abierto}</div>
          </div>
        </div>
      )}

      <div className="section-card">
        <h2>Presupuesto protegido</h2>
        <table>
          <thead>
            <tr>
              <th>Tratamiento</th>
              <th>Lista</th>
              <th>Medio</th>
              <th>Precio final</th>
              <th>Piso</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {protectedQuotes.map((q) => {
              const product = products.find((p) => String(p.id) === String(q.product_id));
              const blocked = q.override_required && !q.override_approved_by;
              const authorized = q.override_required && q.override_approved_by;
              return (
                <tr key={q.id}>
                  <td>{product?.name || `#${q.product_id}`}</td>
                  <td>{formatCurrency(q.list_price)}</td>
                  <td>{q.payment_method}</td>
                  <td>{formatCurrency(q.final_price)}</td>
                  <td>{formatCurrency(q.protected_floor)}</td>
                  <td>
                    {blocked ? (
                      <span className="bad">BLOQUEAR</span>
                    ) : authorized ? (
                      <span className="warn">AUTORIZADO</span>
                    ) : (
                      <span className="ok">HABILITADO</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {protectedQuotes.length === 0 && (
              <tr>
                <td colSpan={6} className="meta">
                  Sin presupuestos registrados todavía.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="section-card">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2>Liquidación vendedoras</h2>
          {isAdminLike && (
            <button className="btn-ghost" disabled={busyId === "gen-sellers"} onClick={generateSellerSettlements}>
              Generar liquidaciones del mes
            </button>
          )}
        </div>
        <table>
          <thead>
            <tr>
              <th>Vendedora</th>
              <th>BCM atribuida</th>
              <th>Base</th>
              <th>Bonus</th>
              <th>Total</th>
              <th>Estado</th>
              {isAdminLike && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {sellerSettlements.map((s) => (
              <tr key={`${s.seller_id}-${s.period_month}`}>
                <td>{userName(s.seller_id)}</td>
                <td>{formatCurrency(s.attributed_bcm)}</td>
                <td>{formatCurrency(s.base_commission)}</td>
                <td>{formatCurrency(s.bonuses)}</td>
                <td>
                  <b>{formatCurrency(s.total_due)}</b>
                </td>
                <td>
                  <span className="pill">{s.status}</span>
                </td>
                {isAdminLike && (
                  <td>
                    {s.id && s.status !== "LOCKED" && (
                      <div className="row" style={{ gap: 6 }}>
                        {s.status !== "APPROVED" && (
                          <button className="link-btn" disabled={busyId === `approve-${s.id}`} onClick={() => approveSellerSettlement(s.id)}>
                            Aprobar
                          </button>
                        )}
                        <button className="link-btn" disabled={busyId === `lock-${s.id}`} onClick={() => lockSellerSettlement(s.id)}>
                          Bloquear
                        </button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {sellerSettlements.length === 0 && (
              <tr>
                <td colSpan={isAdminLike ? 7 : 6} className="meta">
                  Sin liquidaciones para este mes.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="section-card">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2>Fondo de Integración</h2>
          {isAdminLike && (
            <button className="btn-ghost" disabled={busyId === "gen-professionals"} onClick={generateProfessionalSettlements}>
              Generar liquidaciones del mes
            </button>
          )}
        </div>
        <table>
          <thead>
            <tr>
              <th>Profesional</th>
              <th>Puntos</th>
              <th>Valor punto</th>
              <th>Premio</th>
              <th>Estado</th>
              {isAdminLike && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {professionalSettlements.map((p) => (
              <tr key={`${p.professional_id}-${p.period_month}`}>
                <td>{professionalName(p.professional_id)}</td>
                <td>{p.points}</td>
                <td>{formatCurrency(p.point_value)}</td>
                <td>
                  <b>{formatCurrency(p.total_due)}</b>
                </td>
                <td>
                  <span className="pill">{p.status}</span>
                </td>
                {isAdminLike && (
                  <td>
                    {p.id && p.status !== "LOCKED" && (
                      <div className="row" style={{ gap: 6 }}>
                        {p.status !== "APPROVED" && (
                          <button className="link-btn" disabled={busyId === `papprove-${p.id}`} onClick={() => approveProfessionalSettlement(p.id)}>
                            Aprobar
                          </button>
                        )}
                        <button className="link-btn" disabled={busyId === `plock-${p.id}`} onClick={() => lockProfessionalSettlement(p.id)}>
                          Bloquear
                        </button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {professionalSettlements.length === 0 && (
              <tr>
                <td colSpan={isAdminLike ? 6 : 5} className="meta">
                  Sin liquidaciones para este mes.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
